import React, { useState } from 'react';

import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

import { Document, Page, pdfjs } from 'react-pdf';
import { useNavigate, useParams } from 'react-router-dom';

import axios from '../../api/axios';

import { FaCalendarAlt } from 'react-icons/fa';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import BottomNav from '../../components/BottomNav';
import HeaderWithBack from '../../components/HeaderWithBack';
import SendEmail from '../../components/modal/SendEmail';
import HospitalSelection from '../../components/modal/HospitalSelection';
import { useCancelOnlinePrescription, useSendPrescriptionEmail } from '../../hooks/usePrescriptionAction';
import { usePrescriptionDetail } from '../../hooks/usePrescriptionDetail';
import { usePrescriptionOrder } from '../../hooks/usePrescriptionOder';
import { usePrescriptionTracking } from '../../hooks/usePrescriptionTracking';
import { useAuthStore } from '../../stores/authStores';
import '../../styles/styles.css'; // Sesuaikan path jika berbeda
import FullscreenLoading from './Components/FullScreenLoader';
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

type TrackingData = {
  order_id: string;
  status: string;
  updated_time: string;
};


const EprescriptionDetail: React.FC = () => {
  const navigate = useNavigate();
  const { prescriptionId } = useParams<{ prescriptionId: string }>();
  const [prescriptionOrder, setPrescriptionOrder] = useState<any>(false);
  const userData = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);
  const email = userData ? userData.userData : null;
  const { data, isLoading, refetch: refetchPrescriptionDetail } = usePrescriptionDetail(prescriptionId,token);
  
  const [showPdf, setShowPdf] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  const [numPages, setNumPages] = useState<number | null>(null);
  const { data: orderData } = usePrescriptionOrder(prescriptionId,token);
  const { data: trackingData } = usePrescriptionTracking(prescriptionId,token);
  const sendEmailMutation = useSendPrescriptionEmail();
  const cancelMutation = useCancelOnlinePrescription();
  const prescriptionData = data?.data;
  const encToken = data?.encToken;
  const prescriptionOrderData = orderData
  const prescriptionOrderTracking: TrackingData[] = trackingData ?? [];

  const publicPdfUrl = prescriptionData?.pdf_url;
  const publicPdfUrlDownload = prescriptionData?.pdf_url_download;
  const handleDownloadPdf = () => {
    if (publicPdfUrlDownload) {
      setShowButton(false);
      setHasDownloaded(true);
      const link = document.createElement('a');
      link.href = publicPdfUrlDownload;
      link.download = 'resep_telekonsultasi.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      console.warn('URL PDF tidak tersedia untuk diunduh.');
    }
  };

  let isExpired = false;
  const orderStatus = prescriptionOrderData?.order?.order_status;
  const paymentStatus = prescriptionOrderData?.transaction?.status_payment;
  const isPaidOrder = orderStatus === 'paid' || paymentStatus === 'payment';
  const order = prescriptionOrderData?.order;
  const transaction = prescriptionOrderData?.transaction;
  const hasNoOrder = !order;
  const canContinuePayment =
    order &&
    (order.order_status === 'draft' ||
    order.order_status === 'waiting_payment') &&
    transaction?.status_payment === 'draft';
  const isPaid =
    order?.order_status === 'paid' ||
    transaction?.status_payment === 'payment';
  const [showCancelOnlineModal, setShowCancelOnlineModal] = useState(false);
  const [showTebusSendiriConfirm, setShowTebusSendiriConfirm] = useState(false);
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [isTebusSendiriMode, setIsTebusSendiriMode] = useState(false);
  const [showHospitalModal, setShowHospitalModal] = useState(false);
  const [selectedHospital, setSelectedHospital] = useState<{ id: string; name: string } | null>(null);
  const [showConfirmHospitalModal, setShowConfirmHospitalModal] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [hasCreatedLog, setHasCreatedLog] = useState(false);
  const [isCreatingLog, setIsCreatingLog] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Auto-show PDF if already downloaded
  React.useEffect(() => {
    if (prescriptionData?.download === 1) {
      setIsTebusSendiriMode(true);
      setShowPdf(true);
      setShowButton(true);
    }
  }, [prescriptionData?.download]);

  if (prescriptionData?.expired) {
    const [day, month, year] = prescriptionData.expired.split("-");
    const expiredDate = new Date(`${year}-${month}-${day}`);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiredDate.setHours(0, 0, 0, 0);

    isExpired = expiredDate < today;
  }

  const handleSendEmail = () => { 
    setShowModal(true)
  }

  const handleConfirm = () => {
    if (!prescriptionId || !token) return;

    sendEmailMutation.mutate(
      { prescriptionId, token },
      {
        onSuccess: () => {
          alert("Berhasil mengirim resep");
          setShowButton(false);
          setShowModal(false);
        },
        onError: () => {
          alert("Gagal mengirim resep");
        },
      }
    );
  }

  const handleCheckout = async () => {
    try {
      navigate(
        `/e-prescription/checkout/${prescriptionId}/${encToken}`
      );
    } catch (err) {
      alert('Gagal memulai checkout');
    }
  };

  const handleTebusSendiri = async () => {
    if (prescriptionOrder) {
      await axios.post(
        `prescription/${prescriptionId}/cancel-online`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    }

    setIsTebusSendiriMode(true);
    setShowPdf(true);
    setShowButton(true);
  };

  const handleHospitalSelect = (hospital: { id: string; name: string }) => {
    setSelectedHospital(hospital);
    setShowConfirmHospitalModal(true);
  };

  const handleConfirmCreateLog = async () => {
    if (!selectedHospital || !prescriptionId || !token) return;

    setIsCreatingLog(true);

    try {
      const payload = {
        prescription_id: parseInt(prescriptionId || '0'),
        hospital_id: parseInt(selectedHospital.id),
        hospital_name: selectedHospital.name
      };

      console.log('Sending payload:', payload);

      await axios.post(
        `prescription/create-log-tebus-sendiri`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setHasCreatedLog(true);
      setShowConfirmHospitalModal(false);
      setShowHospitalModal(false);
      await refetchPrescriptionDetail();
    } catch (error) {
      console.error('Error creating LOG:', error);
      alert('Gagal membuat LOG. Silakan coba lagi.');
    } finally {
      setIsCreatingLog(false);
    }
  };

  const handleConfirmTebusSendiri = () => {
    if (!prescriptionId || !token) return;

    setIsCancelLoading(true);

    cancelMutation.mutate(
      { prescriptionId, token },
      {
        onSuccess: () => {
          setShowCancelOnlineModal(false);
          setShowHospitalModal(true);
        },
        onError: () => {
          alert("Gagal membatalkan tebus online");
        },
        onSettled: () => {
          setIsCancelLoading(false);
        },
      }
    );
  };

  return (
    <div className="min-h-screen bg-white flex flex-col pb-20">
      {/* Header */}
      <HeaderWithBack title="E-Prescription" url="/e-prescription"/>

      {/* Info Resep */}
      <div className="bg-teal-500 text-white px-4 py-2 flex items-center">
        <FaCalendarAlt className="mr-2" />
        <span>Resep Online Anda</span>
      </div>
      
      {prescriptionData?.expired && (!prescriptionOrder || prescriptionData?.download !== 1) && (
        isExpired ? (
          <div className="bg-red-500 text-white px-4 py-2 text-sm text-center">
            Resep Sudah Kadaluarsa <br /> <strong>{prescriptionData.expired}</strong>
          </div>
        ) : (
          <div className="bg-yellow-100 text-yellow-800 px-4 py-2 text-sm text-center">
            Lakukan penebusan obat sebelum <br /> <strong>{prescriptionData.expired}</strong>
          </div>
        )
      )}

      {/* Resep Digital */}
      <div className="px-4 mt-4">
        <div className="bg-white border rounded-md shadow-sm p-4">
          <h2 className="font-semibold text-gray-800 mb-2">Resep Digital</h2>
          <div className="flex items-center border rounded-md p-3 mb-3">
            <img
              src={prescriptionData?.avatar_doctor}
              alt="doctor"
              className="w-10 h-10 rounded-full mr-3"
            />
            <div>
              <p className="font-semibold text-sm">{prescriptionData?.doctor}</p>
              <p className="text-xs text-gray-500">{prescriptionData?.specialty}</p>
            </div>
          </div>

          <div className="flex justify-between items-center text-sm mb-2">
            <p className="font-semibold">R/ {prescriptionData?.code}</p>
            <p className="text-gray-500">Tanggal Konsultasi: <br /> {prescriptionData?.date}</p>
          </div>

          {prescriptionData?.items?.map((item: any, index: number) => (
            <div
              key={index}
              className="border px-3 py-2 rounded-md text-sm bg-gray-50 mb-2"
            >
              {item?.drug_name} x {item?.qty} {item?.unit} ({item?.frequency} x {item?.frequency_dd})
            </div>
          ))}

          {/* Catatan Alergi */}
          <div className="mt-4">
            <h3 className="font-semibold text-sm mb-2">Catatan Alergi :</h3>
            { prescriptionData?.allergies.length ? prescriptionData?.allergies?.map((alergi: any, index: number) => (
              <div key={index} className="border rounded-md p-2 mb-2 text-sm">
                <p className="font-semibold">{alergi?.allergy}</p>
                <p className="text-gray-500">Catatan : {alergi?.allergy_note || '-'}</p>
              </div>
            )) : (
              <p className="text-gray-500">Tidak ada catatan alergi</p>
            ) }
          </div>

          {/* LOG Code */}
          {prescriptionData?.log_code && (
            <div className="mt-4">
              {prescriptionData?.hospital_name && (
                <div className="mb-2">
                  <h3 className="font-semibold text-sm mb-1">Rumah Sakit Tujuan :</h3>
                  <p className="text-sm text-gray-700">{prescriptionData.hospital_name}</p>
                </div>
              )}
              <h3 className="font-semibold text-sm mb-2">LOG Code :</h3>
              <div className="border rounded-md p-2 bg-blue-50">
                <p className="text-sm font-mono font-semibold text-blue-800">{prescriptionData.log_code}</p>
              </div>
            </div>
          )}
        </div>
      </div>
      {isPaidOrder ? (
      <>
      
    {/* Detail Pengiriman */}
    <div className="px-4 mt-4">
      <div className="border shadow rounded bg-white">
        <div className="flex p-4">
          {/* Ikon sesuai tampilan original */}
          <img
            src="https://m.linksehat.com/assets/img/icons/delivery-order-icon.png"
            alt="Delivery Icon"
            className="h-[30px]"
          />
          <div className="px-4 self-center text-gray-700 w-full">
            Detail Pengirim
          </div>
          <div className="px-4 self-center text-gray-700 ml-auto">
            Apotek K24
          </div>
        </div>

        <div className="px-4 pb-4">
          <div>
            <span className="font-bold">{prescriptionOrderData.order?.outlet}</span>
          </div>
          <div>
            <span>{prescriptionOrderData.order?.shipping_method}</span>
          </div>
        </div>
      </div>
    </div>

    <div className="px-4 mt-4">
      <div className="bg-white border rounded-md shadow-sm p-4">
        {/* Header dengan Icon dan Judul */}
        <div className="flex items-center mb-3">
          <div className="bg-teal-100 text-teal-600 p-2 rounded-full mr-2">
            <i className="fas fa-box-open"></i>
          </div>
          <h2 className="font-semibold text-gray-800 text-sm">Detail Paket</h2>
        </div>

        {/* Detail Pesanan */}
        <div className="text-sm mb-4">
          <p className="font-semibold mb-1">Detail Pesanan :</p>
          <div className="pl-2">
            {prescriptionData?.items?.map((item: any, index: number) => (
              <p>{item?.drug_name}</p>
            ))}
          </div>
        </div>

        {/* Biaya dan Total */}
        <div className="text-sm mb-4">
          <div className="flex justify-between">
            <span>Biaya Pengiriman</span>
            <span>Rp {prescriptionOrderData.order?.shipping_cost.toLocaleString('id-ID')}</span>
          </div>
          <div className="flex justify-between font-bold">
            <span>Total Pesanan</span>
            <span>Rp {prescriptionOrderData.order?.shipping_cost.toLocaleString('id-ID')}</span>
          </div>
        </div>

        {/* Metode Pembayaran */}
        <div className="text-sm mb-2">
          <p className="font-semibold mb-1">Metode Pembayaran :</p>
          <div className="pl-2">
            <p><span>Biller Code: </span>{prescriptionOrderData.transaction?.merchant_code}</p>
            <p><span>Bill Key: </span>{prescriptionOrderData.transaction?.va}</p>
              {prescriptionOrderData.transaction?.status_payment !== 'payment' ? (
                <p><span>Bayar Sebelum : </span>{prescriptionOrderData.transaction?.expired_date}</p>
              ) : null 
              }
          </div>
        </div>

        {/* Total Bayar & Status */}
        <div className="text-sm">
          <div className="flex justify-between font-bold">
            <span>Total Bayar:</span>
            <span>Rp {prescriptionOrderData.order?.shipping_cost.toLocaleString('id-ID')}</span>
          </div>
          <p className="mt-1">
            <span className="font-bold">Status: </span>
            {prescriptionOrderData.transaction?.status_payment === 'payment' ? (
              <span className="text-green-600">Sudah Dibayar</span>
            ) : (
              <span className="font-bold">Menunggu Pembayaran</span>
            )}
            
          </p>
        </div>
      </div>
  </div>

  <div className="px-4 mt-4">
    <div className="bg-white border rounded-md shadow-sm p-4">
      {/* Header Icon & Title */}
      <div className="flex items-center mb-3">
        <div className="bg-teal-100 text-teal-600 p-2 rounded-full mr-2">
          <i className="fas fa-truck"></i>
        </div>
        <h2 className="font-semibold text-gray-800 text-sm">Status Pengiriman</h2>
      </div>

      {/* List Status */}
      <ul className="text-sm space-y-2 pl-2">
        {isLoading ? (
          <li className="text-gray-500 italic">Memuat status...</li>
        ) : prescriptionOrderTracking.length > 0 ? (
          prescriptionOrderTracking.map((data, key) => (
            <li key={key} className="flex flex-col items-start">
              <div className="flex items-start">
                <span
                  className={`h-3 w-3 rounded-full mr-2 mt-1 bg-teal-500`}
                ></span>
                <span className="text-gray-700">{data.status}</span>
              </div>
              <span className="text-xs text-gray-500 pl-5">{data.updated_time}</span>
            </li>
          ))
        ) : (
          <li className="text-gray-500">Belum ada status.</li>
        )}
      </ul>
    </div>

    {/* WhatsApp Button */}
    <div className="mt-4 flex justify-center">
      <a
        href="https://wa.me/6281390002249?text=Halo%20Admin%2C%20saya%20ingin%20bertanya%20tentang%20status%20pengiriman%20obat"
        target="_blank"
        rel="noopener noreferrer"
        className="bg-teal-500 text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center"
      >
        <i className="fab fa-whatsapp mr-2"></i> HUBUNGI VIA WHATSAPP
      </a>
    </div>
  </div>
  </>
  ) : ''
  }
  
  {/* Tombol Tebus Online & Tebus Sendiri */}
  {/* {isLoading ?
  <FullscreenLoading />
  :
  !isLoading && !isExpired && (
      <div className="mt-4">
        <div className="flex p-4">
          <div className="flex">
            <button
              type="submit"
              onClick={() => navigate(`/e-prescription/checkout/${prescriptionId}/${encToken}`)}
              className="p-2 mr-2 bg-teal-500 rounded text-white whitespace-nowrap flex items-center"
            >
              <i className="fas fa-shopping-cart mr-2"></i> Tebus Online
            </button>
            <button
              onClick={handleTebusSendiri}
              type="button"
              className="p-2 bg-teal-600 rounded text-white text-sm flex items-center"
            >
              <i className="fas fa-download mr-2"></i> Tebus Sendiri
            </button>
          </div>
        </div>
      </div>
    )}
    {!isLoading && !isExpired && hasNoOrder && (
  <div className="mt-4 p-4 flex gap-2">
    <button
      onClick={() =>
        navigate(`/e-prescription/checkout/${prescriptionId}/${encToken}`)
      }
      className="p-2 bg-teal-500 rounded text-white flex items-center"
    >
      <i className="fas fa-shopping-cart mr-2"></i>
      Tebus Online
    </button>

    <button
      onClick={handleTebusSendiri}
      className="p-2 bg-teal-600 rounded text-white flex items-center"
    >
      Tebus Sendiri
    </button>
  </div>
)} */}

{isLoading && <FullscreenLoading />}

{!isLoading && !isExpired && !isPaid && (
  <div className="mt-4 p-4 flex gap-2">
    {hasNoOrder && !hasDownloaded && !hasCreatedLog && !prescriptionData?.log_code && prescriptionData?.download !== 1 && (
      <>
        <button
          onClick={() => setShowTebusSendiriConfirm(true)}
          className="p-2 bg-teal-500 rounded text-white flex items-center"
        >
          Tebus di Apotek (Non - Primaya)
        </button>

        <button
          onClick={() => setShowHospitalModal(true)}
          className="p-2 bg-teal-500 rounded text-white flex items-center"
        >
          Tebus ke Primaya
        </button>
      </>
    )}
    {canContinuePayment && (
      <>
        <button
          onClick={() =>
            navigate(`/e-prescription/checkout/${prescriptionId}/${encToken}`)
          }
          className="p-3 bg-orange-500 text-white rounded font-semibold"
        >
          <i className="fas fa-credit-card mr-2"></i>
          Lanjutkan Pembayaran
        </button>

        <button
          onClick={() => setShowCancelOnlineModal(true)}
          className="p-2 bg-teal-600 rounded text-white flex items-center"
        >
          Tebus Sendiri
        </button>
      </>
    )}
    {showCancelOnlineModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg w-[90%] max-w-md p-6">
          <h3 className="text-lg font-semibold mb-2">
            Ganti ke Tebus Sendiri?
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Jika kamu melanjutkan, proses tebus online akan dibatalkan dan
            kamu tidak bisa melanjutkan pembayaran secara online.
          </p>
          <div className="flex justify-end gap-2">
            <button
              disabled={isCancelLoading}
              onClick={() => setShowCancelOnlineModal(false)}
              className="px-4 py-2 rounded bg-gray-200 text-gray-700"
            >
              Batal
            </button>

            <button
              disabled={isCancelLoading}
              onClick={handleConfirmTebusSendiri}
              className="px-4 py-2 rounded bg-red-600 text-white"
            >
              {isCancelLoading ? 'Memproses...' : 'Ya, Tebus Sendiri'}
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
)}

    {isExpired && prescriptionData?.download !== 1 && (
      <div className="mt-4">
        <div className="flex p-4">
          <div className="flex">
            <button
              type="submit"
              onClick={() => navigate(`/home`)}
              className="p-2 mr-2 bg-teal-500 rounded text-white whitespace-nowrap flex items-center"
            >
              <i className="fas fa-sync-alt mr-2"></i> Konsultasi Ulang
            </button>
          </div>
        </div>
      </div>
    )}
     
    {showPdf && publicPdfUrl && isTebusSendiriMode ? (
      <div className="mt-4 border rounded p-4 bg-white">
        <Document
          file={publicPdfUrl}
          onLoadSuccess={({ numPages }) => setNumPages(numPages)}
        >
          {Array.from(new Array(numPages), (el, index) => (
            <Page
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              width={window.innerWidth - 64}
            />
          ))}
        </Document>
        {!isExpired && showButton && prescriptionData?.download !== 1 ? (
            <div className="mt-4">
              <div className="flex flex-wrap gap-2 p-4">
                {/* Tombol Kirim Online */}
                <button
                  type="submit"
                  onClick={handleSendEmail}
                  className="p-2 bg-teal-500 rounded text-white whitespace-nowrap flex items-center"
                >
                  <i className="fas fa-paper-plane mr-2"></i> Kirim Email
                </button>

                {/* Tombol Download */}
                <button
                  onClick={handleDownloadPdf}
                  type="button"
                  className="p-2 bg-teal-600 rounded text-white text-sm flex items-center"
                >
                  <i className="fas fa-file-download mr-2"></i> Download
                </button>

                {/* Tombol Batal */}
                <button
                  onClick={() => setShowPdf(false)}
                  type="button"
                  className="p-2 bg-gray-500 rounded text-white text-sm flex items-center"
                >
                  <i className="fas fa-times-circle mr-2"></i> Batal
                </button>

                <SendEmail
                  isOpen={showModal}
                  onClose={() => setShowModal(false)}
                  onConfirm={handleConfirm}
                  email={email}
                />
              </div>
            </div>
          ) : null}

        
        
      </div>
    ) : null}



      {/* Bottom Navigation */}
      <BottomNav />

      <HospitalSelection
        isOpen={showHospitalModal}
        onClose={() => setShowHospitalModal(false)}
        onHospitalSelect={handleHospitalSelect}
        token={token || ''}
      />

      {showTebusSendiriConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg w-[90%] max-w-md p-6">
            <div className="flex items-center gap-2 mb-2">
              <i className="fas fa-exclamation-triangle text-yellow-500"></i>
              <h3 className="text-lg font-semibold">Tebus di Apotek (Non - Primaya)?</h3>
            </div>
            <p className="text-sm text-gray-600 mb-2">Sebelum melanjutkan, perlu diketahui:</p>
            <ul className="text-sm text-gray-600 space-y-1 pl-4 mb-4 list-disc">
              <li>Resep akan ditampilkan dan bisa diunduh</li>
              <li className="text-red-600 font-medium">
                Setelah resep diunduh, kamu <b>tidak bisa</b> memilih Tebus ke Primaya
              </li>
              <li>Bawa resep ke apotek terdekat untuk menebus obat</li>
            </ul>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowTebusSendiriConfirm(false)}
                className="px-4 py-2 rounded bg-gray-200 text-gray-700"
              >
                Batal
              </button>
              <button
                onClick={() => {
                  setShowTebusSendiriConfirm(false);
                  handleTebusSendiri();
                }}
                className="px-4 py-2 rounded bg-red-600 text-white"
              >
                Ya, Tebus Sendiri
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirmHospitalModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg w-[90%] max-w-md p-6">
            <h3 className="text-lg font-semibold mb-2">
              Konfirmasi Tebus ke Primaya
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Apakah anda yakin melanjutkan tebus resep ke <span className="font-bold">{selectedHospital?.name}</span>?
              <br/><br/>
              <span className="text-amber-600 font-medium italic">Warning: Setelah dipilih tidak bisa diganti.</span>
            </p>
            <div className="flex justify-end gap-2">
              <button
                disabled={isCreatingLog}
                onClick={() => setShowConfirmHospitalModal(false)}
                className="px-4 py-2 rounded bg-gray-200 text-gray-700"
              >
                Batal
              </button>

              <button
                disabled={isCreatingLog}
                onClick={handleConfirmCreateLog}
                className="px-4 py-2 rounded bg-teal-600 text-white flex items-center justify-center min-w-[120px]"
              >
                {isCreatingLog ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Memproses...
                  </>
                ) : (
                  'Ya, Lanjutkan'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl w-[85%] max-w-sm p-6 text-center shadow-xl">
            <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-check-circle text-teal-500 text-3xl"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Berhasil!</h3>
            <p className="text-sm text-gray-600 mb-6">
              LOG awal berhasil dibuat. Silakan cek LOG Code di detail resep.
            </p>
            <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full py-3 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-xl text-sm transition-colors"
            >
              Mengerti
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EprescriptionDetail;
